/**
 * The deterministic scripted pilot and the headless fast-forward behind
 * `window.__lumen.setRunTime`.
 *
 * Why this file exists. The debug hook used to teleport the clock: `loop.setTick(t)`
 * plus `world.tick = t`, and nothing else. The director's bank, its schedule cursor,
 * the player's level and build, and the field itself all stayed at minute 0, so every
 * "17:00" capture photographed a minute-0 run wearing a 17:05 timestamp — three enemies
 * and "LV 1" at what §8.13 calls the climax. There is exactly one way to produce a
 * truthful late-game state out of a budget-driven director (§8.1) and an XP-driven
 * progression curve (§9.3): simulate the run. So this module does, 60 ticks a second,
 * with somebody at the stick.
 *
 * Three properties are load-bearing.
 *
 *  - **It consumes no RNG.** The pilot is a closed-form function of `w.tick` and of
 *    sim state it only reads. It never touches `w.rng.*`, so seeking to tick T leaves
 *    every `spawn` / `loot` / `ai` / `crit` draw exactly where an unseeked run would
 *    have left it. Same seed + same target tick ⇒ byte-identical world (INTERFACES.md
 *    ground rule 1, §8.12).
 *  - **It never renders.** `fastForward` drives `world.step` and `bus.flush` directly
 *    and skips the loop, the renderer and the frame path entirely; the integrator
 *    detaches presentation around the call.
 *  - **It resolves its own level-ups.** R5 opens the draw and sets `world.paused`
 *    inside step 13, so a fast-forward that ignored the modal would deadlock on the
 *    first level-up at ~0:40. The pilot picks through `applyPick` — the same entry
 *    point the UI uses — with a fixed preference order, so it arrives at minute 17
 *    holding a real four-weapon build instead of the starter lance.
 */

import { MAX_WEAPON_LEVEL, passiveById, weaponById } from '../core/content';
import type { World, RunOutcome } from './world';
import type { Pickup } from './entities';
import { dashReady } from './player';
import { MAX_PASSIVE_SLOTS, MAX_WEAPON_SLOTS, applyPick, currentOffers, isUnlocked } from './progression';
import { wrapDeltaX, wrapDeltaY, wrapDist2 } from './wrap';

// --- pilot tuning (every value is a constant; none of it is drawn) ---

/** Radius of the patrol figure-eight, in world units. The arena is 200×200 (±100). */
export const PILOT_ORBIT_RADIUS = 44;
/** One lap per 60 s. Holds the goal point at ~5.3 u/s against the player's 8 u/s top speed. */
export const PILOT_ORBIT_TICKS = 3600;
/** The goal traces `y = A·sin(2θ)`, so the patrol is a figure-eight, not a circle. */
export const PILOT_ORBIT_ASPECT = 0.62;
/** Enemies inside this ring push the pilot away — the kiting term. */
export const PILOT_AVOID_RADIUS = 9;
/**
 * Motes inside this ring pull the pilot back over its own kill trail. Wide, because
 * §9.3.3's income model only lands its checkpoints if most of the drop is actually
 * picked up — motes despawn 60 s after they land (§10.9) and a pilot that never
 * doubles back leaves two thirds of the run's XP on the floor.
 */
export const PILOT_MOTE_RADIUS = 32;
/** Re-pick the mote to chase twice a second; the choice is cached in between. */
export const PILOT_MOTE_RESCAN_TICKS = 30;
/** A dash fires when something is this close. */
export const PILOT_DASH_THREAT_RADIUS = 7;
/** …and otherwise whenever the dash has been idle this long, so it is never wasted. */
export const PILOT_DASH_IDLE_TICKS = 180;

const W_PATROL = 0.7;
const W_AVOID = 1.5;
const W_MOTE = 1.0;

/** Guard against a malformed draw looping `applyPick` forever. */
const MAX_PICKS_PER_TICK = 64;

// --- per-world pilot state (deterministic: written only from sim reads) ---

interface PilotState {
  moteId: number;
  moteX: number;
  moteY: number;
  moteScanTick: number;
  lastDashTick: number;
  /** The next tick this pilot has not driven yet, so consecutive seeks do not overlap. */
  nextTick: number;
}

const PILOTS = new WeakMap<World, PilotState>();

function pilotOf(w: World): PilotState {
  let st = PILOTS.get(w);
  if (st === undefined) {
    st = { moteId: 0, moteX: 0, moteY: 0, moteScanTick: -1, lastDashTick: -PILOT_DASH_IDLE_TICKS, nextTick: 0 };
    PILOTS.set(w, st);
  }
  return st;
}

/** Clears the pilot's memory. Call alongside `world.reset(seed)`. */
export function resetAutopilot(w: World): void {
  const st = pilotOf(w);
  st.moteId = 0;
  st.moteX = 0;
  st.moteY = 0;
  st.moteScanTick = -1;
  st.lastDashTick = -PILOT_DASH_IDLE_TICKS;
  st.nextTick = 0;
}

// --- the pilot ---

/**
 * Picks the mote to chase inside {@link PILOT_MOTE_RADIUS}, scored `value / (4 + d)` so
 * a 25-XP bloom two lengths further out beats a 1-XP shard underfoot (§9.2.1). Iterates
 * `liveIndices()` ascending, so ties break on pool index and never on a Map walk.
 */
function rescanMote(w: World, st: PilotState): void {
  const px = w.player.pos.x;
  const py = w.player.pos.y;
  const list = w.pickups.liveIndices();
  const maxD2 = PILOT_MOTE_RADIUS * PILOT_MOTE_RADIUS;
  let bestScore = 0;
  let best: Pickup | null = null;
  for (let i = 0; i < list.length; i++) {
    const p = w.pickups.slots[list[i]];
    if (!p.alive || p.pickup_kind !== 'xp_mote') continue;
    const d2 = wrapDist2(px, py, p.pos.x, p.pos.y);
    if (d2 > maxD2) continue;
    const score = p.value / (4 + Math.sqrt(d2));
    if (score > bestScore) {
      bestScore = score;
      best = p;
    }
  }
  if (best === null) {
    st.moteId = 0;
    return;
  }
  st.moteId = best.id;
  st.moteX = best.pos.x;
  st.moteY = best.pos.y;
}

/** Keeps a cached mote target alive across the rescan gap. */
function refreshMote(w: World, st: PilotState): boolean {
  if (st.moteId === 0) return false;
  const list = w.pickups.liveIndices();
  for (let i = 0; i < list.length; i++) {
    const p = w.pickups.slots[list[i]];
    if (p.id !== st.moteId) continue;
    if (!p.alive || p.pickup_kind !== 'xp_mote') break;
    st.moteX = p.pos.x;
    st.moteY = p.pos.y;
    return true;
  }
  st.moteId = 0;
  return false;
}

/**
 * Sim step 0 for a fast-forward: writes `world.input` for this tick.
 *
 * Three terms, summed and clamped to a unit wish vector: a patrol goal that walks a
 * figure-eight so the swarm has something to chase and the §8.5 formations get a
 * moving anchor to compose around; a repulsion term over every enemy inside
 * {@link PILOT_AVOID_RADIUS}, which is the kite; and an attraction term toward the
 * nearest mote, which is the only reason the XP curve moves at all. Dash fires on
 * contact pressure, and otherwise off cooldown.
 */
export function stepAutopilotInput(w: World): void {
  const p = w.player;
  const inp = w.input;
  const st = pilotOf(w);
  const tick = w.tick;

  // 1 — patrol goal.
  const theta = (tick / PILOT_ORBIT_TICKS) * Math.PI * 2;
  const gx = Math.cos(theta) * PILOT_ORBIT_RADIUS;
  const gy = Math.sin(theta * 2) * PILOT_ORBIT_RADIUS * PILOT_ORBIT_ASPECT;
  let dx = wrapDeltaX(gx - p.pos.x);
  let dy = wrapDeltaY(gy - p.pos.y);
  let m = Math.sqrt(dx * dx + dy * dy);
  if (m > 1e-4) {
    dx /= m;
    dy /= m;
  } else {
    dx = 1;
    dy = 0;
  }
  let vx = dx * W_PATROL;
  let vy = dy * W_PATROL;

  // 2 — kite away from contact, and note the nearest threat for the dash rule.
  let ax = 0;
  let ay = 0;
  let nearest2 = Number.POSITIVE_INFINITY;
  const enemies = w.enemies.liveIndices();
  for (let i = 0; i < enemies.length; i++) {
    const e = w.enemies.slots[enemies[i]];
    if (!e.alive || e.death_anim > 0) continue;
    const ex = wrapDeltaX(p.pos.x - e.pos.x);
    const ey = wrapDeltaY(p.pos.y - e.pos.y);
    const d2 = ex * ex + ey * ey;
    if (d2 < nearest2) nearest2 = d2;
    if (d2 >= PILOT_AVOID_RADIUS * PILOT_AVOID_RADIUS || d2 < 1e-8) continue;
    const d = Math.sqrt(d2);
    const weight = (PILOT_AVOID_RADIUS - d) / PILOT_AVOID_RADIUS;
    ax += (ex / d) * weight;
    ay += (ey / d) * weight;
  }
  const am = Math.sqrt(ax * ax + ay * ay);
  if (am > 1e-6) {
    vx += (ax / am) * W_AVOID;
    vy += (ay / am) * W_AVOID;
  }

  // 3 — sweep the kill trail for motes.
  if (tick - st.moteScanTick >= PILOT_MOTE_RESCAN_TICKS || !refreshMote(w, st)) {
    st.moteScanTick = tick;
    rescanMote(w, st);
  }
  if (st.moteId !== 0) {
    const mx = wrapDeltaX(st.moteX - p.pos.x);
    const my = wrapDeltaY(st.moteY - p.pos.y);
    const md = Math.sqrt(mx * mx + my * my);
    if (md > 1e-4) {
      vx += (mx / md) * W_MOTE;
      vy += (my / md) * W_MOTE;
    }
  }

  const vm = Math.sqrt(vx * vx + vy * vy);
  if (vm > 1e-6) {
    inp.moveX = vx / vm;
    inp.moveY = vy / vm;
  } else {
    inp.moveX = 0;
    inp.moveY = 0;
  }
  inp.focusHeld = false;

  // 4 — dash: under pressure, or off cooldown so it is never left idle.
  const pressed =
    dashReady(w) &&
    (nearest2 <= PILOT_DASH_THREAT_RADIUS * PILOT_DASH_THREAT_RADIUS ||
      tick - st.lastDashTick >= PILOT_DASH_IDLE_TICKS);
  inp.dashPressed = pressed;
  if (pressed) st.lastDashTick = tick;
}

// --- level-up resolution ---

/** True when `passiveId` is the `linked_passive_id` of a weapon the player already holds. */
function unlocksAnEvolution(w: World, passiveId: string): boolean {
  const p = w.player;
  for (let i = 0; i < p.weapon_count; i++) {
    const slot = p.weapons[i];
    if (slot.def_id === '' || slot.evolved) continue;
    const def = weaponById.get(slot.def_id);
    if (def !== undefined && def.linked_passive_id === passiveId) return true;
  }
  return false;
}

/**
 * True when some owned, unevolved weapon is still waiting on a pairing passive this save
 * can actually be dealt.
 *
 * Rule 3b spends slots on whatever passive is in front of it, and rule 2 is gated on a
 * free slot — so without this, four generic passives fill the rack and the pairing can
 * never be taken afterwards. §9.4.4 gates every evolution behind that pairing, so the
 * seek then reaches the climax with none.
 *
 * The unlock check is what makes the reservation release. Of the four §9.9.1 starter
 * weapons only the Lance pairs with a starter passive; the Krillscatter, the Halo and the
 * Nova pair with Salvo Glands, Bloomfield Organ and Recoil Mantle, none of which a run-1
 * pool can deal. Reading `linked_passive_id` without asking whether it is unlocked holds
 * the fourth slot open for a card that will never come, and a seek then lands three
 * passives even on the seeds where it took the pairing on pick two — 27 of 40 seeds ended
 * at four passives instead of 39.
 */
function awaitingPairing(w: World): boolean {
  const p = w.player;
  for (let i = 0; i < p.weapon_count; i++) {
    const slot = p.weapons[i];
    if (slot.def_id === '' || slot.evolved) continue;
    const def = weaponById.get(slot.def_id);
    if (def === undefined) continue;
    const linked = def.linked_passive_id;
    if (linked === undefined || linked === '' || !isUnlocked(linked)) continue;
    let owned = false;
    for (let j = 0; j < p.passive_count; j++) {
      if (p.passives[j].def_id === linked) {
        owned = true;
        break;
      }
    }
    if (!owned) return true;
  }
  return false;
}

/**
 * The weapon this run is driving toward an evolution: owned, unevolved, below max level,
 * and paired with a passive this save can be dealt. Oldest slot first, so the choice never
 * flips mid-run.
 *
 * §9.4.4 asks for both halves, max level *and* the pairing owned, and rule 4 used to take
 * whichever upgrade sat first in the draw. Four weapons splitting the ~16 upgrade picks a
 * 17:00 seek can afford leaves every one of them around level 4, so the seeds that did draw
 * the pairing still had nothing eligible to evolve: the pilot owned Bristlemouth Instinct on
 * two thirds of the seeds sampled and evolved on three of sixteen.
 */
function evolutionTarget(w: World): string {
  const p = w.player;
  for (let i = 0; i < p.weapon_count; i++) {
    const slot = p.weapons[i];
    if (slot.def_id === '' || slot.evolved || slot.level >= MAX_WEAPON_LEVEL) continue;
    const def = weaponById.get(slot.def_id);
    if (def === undefined || def.evolution_id === undefined) continue;
    const linked = def.linked_passive_id;
    if (linked === undefined || linked === '' || !isUnlocked(linked)) continue;
    return slot.def_id;
  }
  return '';
}

/**
 * The pilot's pick, in a fixed preference order. Every rule reads sim state only, so
 * the sequence is a pure function of the run.
 *
 *  1. **An evolution.** §9.4.1 guarantees it as card 1 whenever one is eligible and
 *     §9.4.4 re-queues it until taken, so declining only stalls the build.
 *  2. **A new passive that is some owned weapon's `linked_passive_id`.** §9.4.4 gates
 *     evolutions behind exactly that pairing, and there are four passive slots against
 *     fourteen passives — leaving the pairing to chance is what produces a 20-minute
 *     run with zero evolutions.
 *  3. **A new weapon** while a weapon slot is open, so the seek lands on the
 *     four-weapon build §6.7 assumes rather than the starter lance.
 *  3a. **An upgrade for the weapon it is evolving**, so one line reaches the max level
 *     §9.4.4 requires instead of four lines stalling one rank short of it.
 *  4. **Any weapon upgrade**, which is what fills the rest of the rack out.
 *  5. **Card 0**, a fair sample of §9.4.1's own 65/35 upgrade-vs-new weighting.
 *
 * Returns `''` when no draw is open.
 */
export function autopilotPick(w: World): string {
  const offers = currentOffers(w);
  if (offers === null || offers.length === 0) return '';
  const p = w.player;

  for (let i = 0; i < offers.length; i++) {
    if (offers[i].kind === 'evolution') return offers[i].id;
  }
  if (p.passive_count < MAX_PASSIVE_SLOTS) {
    for (let i = 0; i < offers.length; i++) {
      const c = offers[i];
      if (c.kind === 'passive' && c.level_from === 0 && unlocksAnEvolution(w, c.id)) return c.id;
    }
  }
  if (p.weapon_count < MAX_WEAPON_SLOTS) {
    for (let i = 0; i < offers.length; i++) {
      if (offers[i].kind === 'weapon' && offers[i].level_from === 0) return offers[i].id;
    }
  }
  // 3a — an upgrade for the weapon this run is evolving, ahead of everything below. Taking
  // upgrades in card order spreads them evenly over four weapons and none of them reaches
  // the max level §9.4.4 requires; committing to one line is what makes the evolution
  // eligible the moment the pairing lands. A 17:00 seek buys about 22 picks and the asserted
  // build costs about 22, so there is no slack to spend on a fourth weapon's fourth rank.
  // Sampled over 40 seeds, this rule took the seek from 3 evolutions in 16 to 35 in 40.
  const evoTarget = evolutionTarget(w);
  if (evoTarget !== '') {
    for (let i = 0; i < offers.length; i++) {
      const c = offers[i];
      if (c.kind === 'weapon' && c.id === evoTarget && c.level_from > 0) return c.id;
    }
  }
  // 3b — any new passive while a passive slot is open. Rule 2 only takes the specific
  // passive that unlocks an evolution, so before this rule the other three slots filled by
  // accident, through rule 5's card-0 fallback. That made the shape of a seeked build a
  // function of how many passives happen to be in the run-1 pool: adding two to §9.9.1
  // dropped a 17:00 seek from four filled slots to one, and a seek that lands three empty
  // slots at the climax is describing a game nobody plays (§6.7).
  // The last slot stays reserved while any owned weapon still wants its pairing passive:
  // rule 2 is gated on a free slot, so filling all four here would lock the pairing out
  // and land the seek at the climax with zero evolutions.
  const genericRoom = awaitingPairing(w) ? MAX_PASSIVE_SLOTS - 1 : MAX_PASSIVE_SLOTS;
  if (p.passive_count < genericRoom) {
    for (let i = 0; i < offers.length; i++) {
      const c = offers[i];
      // `kind` alone is not enough: §9.4.5's Mote Cache and Hull Patch are dealt as passive
      // cards with no passive behind them, and taking one would spend the pick on nothing.
      if (c.kind === 'passive' && c.level_from === 0 && passiveById.has(c.id)) return c.id;
    }
  }
  for (let i = 0; i < offers.length; i++) {
    if (offers[i].kind === 'weapon' && offers[i].level_from > 0) return offers[i].id;
  }
  // The reservation has to survive this fallback too. Card 0 is a fair sample of §9.4.1's
  // own weighting and is often a new passive, so taking it blind fills the slot rule 2 was
  // holding — which is how a seek reaches 17:00 with four maxed weapons, four passives and
  // no evolution, the pairing having never had anywhere to go.
  if (p.passive_count >= genericRoom) {
    for (let i = 0; i < offers.length; i++) {
      const c = offers[i];
      if (c.kind !== 'passive' || c.level_from > 0) return c.id;
    }
  }
  return offers[0].id;
}

/**
 * Drains every open draw. Several levels can bank in one tick, and `applyPick` opens
 * the next draw rather than unpausing, so this loops until the modal is closed.
 */
export function resolveAutopilotOffers(w: World): number {
  let n = 0;
  while (n < MAX_PICKS_PER_TICK) {
    const id = autopilotPick(w);
    if (id === '') break;
    applyPick(w, id);
    n++;
  }
  return n;
}

// --- the fast-forward ---

export interface FastForwardResult {
  /** Tick the seek started from. */
  from: number;
  /** Tick requested. */
  target: number;
  /** Tick actually reached — below `target` only when the run ended early. */
  reached: number;
  /** Sim ticks stepped. */
  ticks: number;
  /** Level-up cards taken during the seek. */
  picks: number;
  /** `running` unless the pilot died or won on the way. */
  outcome: RunOutcome;
}

export type ProgressFn = (tick: number, target: number) => void;

/**
 * Steps the sim from its current tick to `targetTick` inclusive, with the pilot driving.
 *
 * `targetTick` is stepped, so a seek to 5:00 (tick 18,000) has already run the §8.7
 * boss spawn the director schedules for exactly that tick. Afterwards `world.tick`
 * equals `targetTick` and the caller owns re-seating `FixedLoop` at `targetTick + 1`.
 *
 * Nothing here renders and nothing here allocates per tick. `onProgress` fires every
 * `progressEvery` ticks and is the caller's business — this module never reads a clock.
 */
export function fastForward(
  w: World,
  targetTick: number,
  onProgress?: ProgressFn,
  progressEvery = 3600,
): FastForwardResult {
  const st = pilotOf(w);
  const from = w.tick;
  // `world.step(t)` assigns `world.tick = t`, so a world that has already been driven
  // sits *on* its last stepped tick rather than after it. Resuming from `world.tick`
  // would step that tick twice and make one 2:00 seek differ from three seeks that add
  // up to 2:00; the pilot's own cursor is what makes consecutive seeks compose.
  const start = st.nextTick > from ? st.nextTick : from;
  const result: FastForwardResult = {
    from,
    target: targetTick,
    reached: from,
    ticks: 0,
    picks: 0,
    outcome: w.outcome,
  };
  if (targetTick < start) return result;

  // The pilot's memory is deliberately *not* cleared here: it is derived from sim state
  // and carries the dash cadence and the mote currently being chased.
  // A draw left open from before the seek would freeze `world.step` on tick one.
  w.paused = false;
  resolveAutopilotOffers(w);

  for (let tick = start; tick <= targetTick; tick++) {
    stepAutopilotInput(w);
    w.step(tick);
    w.bus.flush(tick);
    result.picks += resolveAutopilotOffers(w);
    result.ticks++;
    result.reached = w.tick;
    st.nextTick = tick + 1;
    if (w.outcome !== 'running') break;
    if (onProgress !== undefined && progressEvery > 0 && tick > start && tick % progressEvery === 0) {
      onProgress(tick, targetTick);
    }
  }

  // The pilot's last wish vector must not survive as a stuck stick once a real input
  // source takes over.
  w.input.moveX = 0;
  w.input.moveY = 0;
  w.input.dashPressed = false;
  w.input.dashBufferedTick = -1;
  result.outcome = w.outcome;
  return result;
}
